import React, { useState, useEffect, useRef } from 'react';
import {
    FiPlus, FiBox, FiSearch, FiEdit, FiTrash2, FiMoreVertical,
    FiImage, FiTag, FiDollarSign, FiLayers, FiCheck, FiX,
    FiTrendingUp, FiStar, FiZap, FiShoppingBag, FiInfo, FiLink, FiCloud, FiHardDrive
} from 'react-icons/fi';
import { productService } from '../../../services/api';
import { categoryService } from '../../../services/api';
import { createProductModel } from '../../../models/productModel';
import './AdminProducts.css';

const generateSlug = (name) =>
    name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');

const parseFlagsFromDescription = (description) => {
    if (!description) return {};
    try {
        // Support new format [[ELLA_DATA:{}]]
        const newMatch = description.match(/\[\[ELLA_DATA:([\s\S]*?)\]\]/);
        if (newMatch && newMatch[1]) {
            return JSON.parse(newMatch[1]);
        }
        // Support old format <!--ELLA_META:{}-->
        const oldMatch = description.match(/<!--ELLA_META:([\s\S]*?)-->/);
        if (oldMatch && oldMatch[1]) {
            return JSON.parse(oldMatch[1]);
        }
    } catch (e) {
        console.warn('Meta parsing failed in AdminProducts');
    }
    return {};
};

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDrawer, setShowDrawer] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [warningMsg, setWarningMsg] = useState('');
    const [imagePreviews, setImagePreviews] = useState([]);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successConfig, setSuccessConfig] = useState({ title: '', message: '' });
    const [targetProduct, setTargetProduct] = useState(null);
    const fileInputRef = useRef();

    // Form State
    const [formStep, setFormStep] = useState(1);
    const [form, setForm] = useState({
        title: '',
        slug: '',
        description: '',
        categoryId: '',
        price: '',
        discount: '',
        stock: '',
        isNewArrival: false,
        isBestSelling: false,
        isTopRated: false,
        isTrending: false,
        isActive: true, // New: standard status control
        sizes: '', // Comma-separated sizes
        colors: '', // Comma-separated colors
        images: [],
        colorImageMap: {} // { "Black": "image_url", "White": "image_url" }
    });

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [prodData, catData] = await Promise.all([
                productService.getAllProducts(),
                categoryService.getAllCategories()
            ]);

            // Use the centralized model for perfect data parity across the app
            setProducts(prodData || []);
            setCategories(catData || []);
        } catch (err) {
            console.error('Data Fetch Error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Reset step when drawer opens/closes
    useEffect(() => {
        if (!showDrawer) {
            setFormStep(1);
        }
    }, [showDrawer]);

    // Auto-dismiss feedback
    useEffect(() => {
        if (successMsg || errorMsg || warningMsg) {
            const t = setTimeout(() => {
                setSuccessMsg('');
                setErrorMsg('');
                setWarningMsg('');
            }, 5000);
            return () => clearTimeout(t);
        }
    }, [successMsg, errorMsg, warningMsg]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Digit Validation for Price, Discount, and Stock
        if (['price', 'discount', 'stock'].includes(name) && type !== 'checkbox') {
            const isPrice = name === 'price';
            // Price allows one decimal, others are pure digits
            const regex = isPrice ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
            if (value !== '' && !regex.test(value)) return;
        }

        setForm(prev => {
            const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
            // Auto-generate slug from title
            if (name === 'title' && !isEditMode) {
                updated.slug = generateSlug(value);
            }
            return updated;
        });
    };

    const handleToggleFlag = (field) => {
        const placementFlags = ['isBestSelling', 'isNewArrival', 'isTopRated'];

        setForm(prev => {
            const newValue = !prev[field];
            const updated = { ...prev, [field]: newValue };

            // If we are turning ON a placement flag, turn OFF all other placement flags
            if (newValue && placementFlags.includes(field)) {
                placementFlags.forEach(f => {
                    if (f !== field) updated[f] = false;
                });
            }

            return updated;
        });
    };

    const compressImage = (file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 800; // Daraz Standard for mobile-first performance
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                // 0.6 quality ensures the payload stays under server limits for 5 images
                resolve(canvas.toDataURL('image/jpeg', 0.6));
            };
        };
    });

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const currentCount = imagePreviews.length;
        if (currentCount >= 5) {
            setErrorMsg('Maximum 5 images allowed for elite presentation.');
            return;
        }

        let filesToProcess = files;
        if (currentCount + files.length > 5) {
            const allowedCount = 5 - currentCount;
            filesToProcess = files.slice(0, allowedCount);
            setWarningMsg(`Limit Reached: Only the first ${allowedCount} images were accepted.`);
        }

        setIsUploadingImages(true);

        const uploadToImgBB = async (base64DataUrl) => {
            try {
                const blob = await (await fetch(base64DataUrl)).blob();
                const fd = new FormData();
                fd.append('key', 'a605aeec82026edda40d3c76675cdb9e');
                fd.append('image', blob, 'upload.jpg');
                const res = await fetch('https://api.imgbb.com/1/upload', {
                    method: 'POST', body: fd,
                    signal: AbortSignal.timeout(10000)
                });
                const data = await res.json();
                if (data?.success && data?.data?.url) return data.data.url;
                return null;
            } catch (err) {
                return null;
            }
        };

        const uploadToTelegraph = async (base64DataUrl) => {
            try {
                const blob = await (await fetch(base64DataUrl)).blob();
                const fd = new FormData();
                fd.append('file', blob);
                const res = await fetch('https://telegra.ph/upload', {
                    method: 'POST', body: fd,
                    signal: AbortSignal.timeout(10000)
                });
                const data = await res.json();
                if (data?.[0]?.src) {
                    const src = data[0].src;
                    const finalUrl = src.startsWith('/') ? `https://telegra.ph${src}` : `https://telegra.ph/${src}`;
                    console.log("Image Cloud Link Generated:", finalUrl);
                    return finalUrl;
                }
                return null;
            } catch (err) {
                console.error("Telegra.ph Network Error:", err);
                return null;
            }
        };

        const processFile = async (file) => {
            const compressed = await compressImage(file);
            if (!compressed) return null;

            // Tier 1: Primary Cloud (ImgBB)
            // Tier 1: Primary Cloud (Telegra.ph - No Key Required, Fast)
            let url = await uploadToTelegraph(compressed);

            // Tier 2: Secondary Cloud (ImgBB - Fallback)
            if (!url) {
                url = await uploadToImgBB(compressed);
            }

            if (url) return { url, source: 'cloud' };

            // ❌ STRICT REJECTION (As requested: No Base64, only cloud URLs)
            console.log("Cloud uploads failed, rejecting image.");
            return { url: 'REJECTED', source: 'rejected' };
        };

        const results = await Promise.all(filesToProcess.map(f => processFile(f)));
        const successful = results.filter(Boolean);

        setIsUploadingImages(false);

        if (successful.length > 0) {
            setImagePreviews(prev => [...prev, ...successful]);
            setForm(prev => ({ ...prev, images: [...prev.images, ...successful.map(r => r.url)] }));
            if (successful.length < filesToProcess.length) {
                setWarningMsg(`Only ${successful.length} of ${filesToProcess.length} images were small enough to process.`);
            }
        } else {
            setErrorMsg('❌ All uploads failed. Please use smaller files or a better connection.');
        }
    };

    const removeImage = (index) => {
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    };

    const handleEditClick = (p) => {
        // Robust image extraction: check images array first, then fallback to singular fields
        let initialImages = [];
        if (Array.isArray(p.images) && p.images.length > 0) {
            initialImages = p.images;
        } else if (p.image || p.imageUrl || p.thumbnail) {
            initialImages = [p.image || p.imageUrl || p.thumbnail];
        }

        // Product Form / Edit Logic
        const storedDescriptions = JSON.parse(localStorage.getItem("ella_product_descriptions")) || {};
        // Prioritize live Database tunneled description over stale local browser cache
        const recoveredDesc = (p.description && p.description !== "No description available") ? p.description : (storedDescriptions[p._id || p.id] || '');

        setForm({
            title: p.title || '',
            slug: p.slug || generateSlug(p.title || ''),

            // ✅ FIX: Clean both old and new markers and prioritize recovered local description
            description: recoveredDesc
                .replace(/\[\[ELLA_DATA:[\s\S]*?\]\]/g, '')
                .replace(/<!--ELLA_META:[\s\S]*?-->/g, '')
                .trim(),

            categoryId: p.categoryId?._id || p.categoryId || '',

            price: p.variants?.[0]?.price || '',
            discount: p.variants?.[0]?.discountValue || 0,
            stock: p.totalStock || 0,

            // ✅ FIX: Use Boolean() for robust flag recovery (handles 1/0 or true/false)
            isTrending: Boolean(p.isTrending),
            isNewArrival: Boolean(p.isNewArrival),
            isBestSelling: Boolean(p.isBestSelling),
            isTopRated: Boolean(p.isTopRated),

            // ✅ Active default true
            isActive: p.isActive !== false,

            sizes: (p.variants || [])
                .map(v => v.size)
                .filter((s, i, a) => s && a.indexOf(s) === i)
                .join(', '),

            colors: (p.variants || [])
                .map(v => v.color)
                .filter((c, i, a) => c && a.indexOf(c) === i)
                .join(', '),

            imageUrlField: '',
            images: initialImages,

            colorImageMap: (p.variants || []).reduce((acc, v) => {
                if (v.color && v.colorImage) acc[v.color] = v.colorImage;
                return acc;
            }, {})
        });

        // Ensure we don't nest objects if p.images somehow contained {url, source} objects from a previous unsynced UI state
        setImagePreviews(initialImages.map(img => {
            if (typeof img === 'string') return { url: img, source: 'cloud' };
            if (img && typeof img === 'object' && img.url) return img;
            return { url: img, source: 'cloud' };
        }));

        setEditId(p._id);
        setIsEditMode(true);
        setShowDrawer(true);
        setFormStep(1);
    };

    const triggerSuccess = (title, message) => {
        setSuccessConfig({ title, message });
        setShowSuccessModal(true);
        // Auto-dismiss after 2.5s for elite feel
        setTimeout(() => setShowSuccessModal(false), 2500);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final sanity check before payload construction
        if (!form.title.trim() || !form.categoryId) {
            setErrorMsg('Title and Category are required.');
            return;
        }

        // 🛡️ STRICT IMAGE VALIDATION (As requested)
        const isImageUrlValid = (url) => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
        if (form.images && form.images.length > 0) {
            const hasInvalidImage = form.images.some(url => !isImageUrlValid(url));
            if (hasInvalidImage) {
                setErrorMsg("Invalid image: Sirf cloud URLs (http/https) allowed hain. Base64 ya local images allowed nahi.");
                return;
            }
        } else if (imagePreviews.some(p => p.source === 'rejected')) {
             setErrorMsg("Invalid image: Sirf cloud URLs (http/https) allowed hain. Base64 ya local images allowed nahi.");
             return;
        }

        setIsSubmitting(true);
        setErrorMsg('');
        setWarningMsg('');

        try {
            // HIGH-PRECISION NUMBER VALIDATION (Solves NaN Rejection)
            const parsedPrice = Number(form.price);
            const basePrice = isNaN(parsedPrice) ? 0 : parsedPrice;

            const parsedDiscount = Number(form.discount);
            const discountVal = isNaN(parsedDiscount) ? 0 : parsedDiscount;

            const parsedStock = Number(form.stock);
            const stockVal = isNaN(parsedStock) ? 0 : parsedStock;

            const validImages = imagePreviews.map(i => i.url);

            // CONSTRUCT BULLETPROOF PAYLOAD
            const payload = {
                title: form.title.trim(),
                slug: form.slug || generateSlug(form.title),
                description: form.description.trim(),
                categoryId: form.categoryId,
                category: form.categoryId,
                isActive: true,

                price: basePrice,
                discount: discountVal,
                totalStock: stockVal,

                isNewArrival: form.isNewArrival,
                isBestSelling: form.isBestSelling,
                isTopRated: form.isTopRated,
                isTrending: form.isTrending,
                isActive: form.isActive,

                // Legacy Compatibility Flags for Backend
                isNewArrivals: form.isNewArrival,
                isBestSeller: form.isBestSelling,
                bestSelling: form.isBestSelling,
                topRated: form.isTopRated,
                newArrival: form.isNewArrival,

                // Simplified Unified Type
                productType: form.isTopRated ? 'topRated' : (form.isBestSelling ? 'bestSelling' : (form.isNewArrival ? 'newArrival' : 'normal')),

                variants: (() => {
                    const sizes = form.sizes.split(',').map(s => s.trim()).filter(Boolean);
                    const colors = form.colors.split(',').map(c => c.trim()).filter(Boolean);

                    const finalSizes = sizes.length > 0 ? sizes : ['Default'];
                    const finalColors = colors.length > 0 ? colors : ['Default'];
                    const variantsCount = finalColors.length * finalSizes.length || 1;

                    // ✅ Smart Distribution: Ensure sum(variants) === stockVal exactly
                    const eachVariantStock = Math.floor(stockVal / variantsCount);
                    const remainder = stockVal % variantsCount;

                    const result = [];
                    let count = 0;
                    finalColors.forEach(c => {
                        const variantColorImage = form.colorImageMap[c] || '';
                        finalSizes.forEach(s => {
                            result.push({
                                color: c,
                                size: s,
                                price: basePrice,
                                // Give the remainder to the very first variant
                                stock: count === 0 ? eachVariantStock + remainder : eachVariantStock,
                                discountValue: discountVal,
                                discountType: 'percent',
                                finalPrice: Number((basePrice - (basePrice * (discountVal / 100))).toFixed(2)),
                                colorImage: variantColorImage
                            });
                            count++;
                        });
                    });
                    // Ghost Variant Data Tunnel
                    const ghostVariant = {
                        color: "ELLA_GHOST_DATA",
                        size: JSON.stringify({
                            isTrending: form.isTrending,
                            isNewArrival: form.isNewArrival,
                            isBestSelling: form.isBestSelling,
                            isTopRated: form.isTopRated,
                            isClearance: form.isClearance || false,
                            images: validImages,
                            colorImageMap: form.colorImageMap || {},
                            realDescription: form.description.trim()
                        }),
                        price: 0,
                        discountType: 'flat',
                        discountValue: 0,
                        stock: 0
                    };
                    
                    result.push(ghostVariant);

                    return result;
                })(),
                images: validImages,
                image: validImages.length > 0 ? validImages[0] : ''
            };

            payload.image = payload.images.length > 0 ? payload.images[0] : '';

            if (isEditMode) {
                await productService.updateProduct(editId, payload);
                triggerSuccess('Product Updated', `"${form.title}" has been updated successfully.`);

                // Persistence Logic for Flags
                const flags = JSON.parse(localStorage.getItem('ella_product_flags') || '{}');
                flags[editId] = {
                    isTrending: form.isTrending,
                    isNewArrival: form.isNewArrival,
                    isBestSelling: form.isBestSelling,
                    isTopRated: form.isTopRated
                };
                localStorage.setItem('ella_product_flags', JSON.stringify(flags));

                // 1. Admin Side: Save Description to localStorage using specific pattern
                const storedDescriptions = JSON.parse(localStorage.getItem("ella_product_descriptions")) || {};
                storedDescriptions[editId] = form.description;
                localStorage.setItem("ella_product_descriptions", JSON.stringify(storedDescriptions));

                // Instant UI update — inject strings for images to maintain state purity
                setProducts(prev => prev.map(p =>
                    p._id === editId
                        ? { ...p, ...payload, ...flags[editId], images: validImages.length > 0 ? validImages : (p.images || []) }
                        : p
                ));
            } else {
                const response = await productService.createProduct(payload);

                // Extremely robust ID extraction because backend structures vary (data.product._id, product._id, data._id, etc.)
                let newId = null;
                const extractId = (obj) => {
                    if (obj && typeof obj === 'object') {
                        if (obj._id) return obj._id;
                        if (obj.product && obj.product._id) return obj.product._id;
                        if (obj.data && obj.data._id) return obj.data._id;
                        if (obj.payload && obj.payload._id) return obj.payload._id;
                        if (obj.result && obj.result._id) return obj.result._id;
                        for (let k in obj) {
                            if (typeof obj[k] === 'object' && obj[k] !== null && obj[k]._id) return obj[k]._id;
                        }
                    }
                    return null;
                };
                newId = extractId(response);



                // 1. Admin Side: Save Flags to localStorage (Create Flow)
                if (newId) {
                    const flags = JSON.parse(localStorage.getItem('ella_product_flags') || '{}');
                    flags[newId] = {
                        isTrending: form.isTrending,
                        isNewArrival: form.isNewArrival,
                        isBestSelling: form.isBestSelling,
                        isTopRated: form.isTopRated
                    };
                    localStorage.setItem('ella_product_flags', JSON.stringify(flags));
                }

                // 1. Prepare flags and images for the local UI updated
                const flagsForNew = newId ? JSON.parse(localStorage.getItem('ella_product_flags') || '{}')[newId] || {} : {
                    isTrending: form.isTrending,
                    isNewArrival: form.isNewArrival,
                    isBestSelling: form.isBestSelling,
                    isTopRated: form.isTopRated
                };
                const finalImgsArray = validImages.length > 0 ? validImages : (payload.images || []);

                // 2. Model the raw response data (Handles description cleaning & flag parsing)
                const modeledData = createProductModel(response?.data || response?.product || response?.payload || { ...payload, _id: Date.now().toString() });

                // 3. Extra merge of local state to handle fields that might not be in the server's immediate response
                const newProduct = {
                    ...modeledData,
                    ...flagsForNew,
                    images: finalImgsArray,
                    // ✅ CRITICAL FIX: Always prioritize the user's input description to avoid "stale" server response emptying it
                    description: form.description.trim()
                };

                setProducts(prev => [newProduct, ...prev]);
                triggerSuccess('Product Added', `"${form.title}" is now live in your store.`);
            }

            setShowDrawer(false);

            // ✅ RESET FORM STATE (Moved outside background refresh)
            setIsEditMode(false);
            setEditId(null);
            setForm({
                title: '', slug: '', description: '', categoryId: '',
                price: '', discount: '', stock: '',
                isNewArrival: false, isTrending: false,
                isTopRated: false, isBestSelling: false, isActive: true,
                sizes: '', colors: '',
                images: [],
                colorImageMap: {}
            });
            setImagePreviews([]);
            setFormStep(1);
        } catch (err) {
            // Show the REAL server error — never hide it
            const statusCode = err.response?.status;
            let serverMsg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                (typeof err.response?.data === 'string' ? err.response.data : null);

            // SPECIAL HANDLING FOR 413 — PAYLOAD TOO LARGE
            if (statusCode === 413) {
                serverMsg = "The images you selected are too large. The server rejects them because of a size limit. Please try using smaller images, or increase the body limit in your Backend (app.js).";
                setWarningMsg("HINT: Add 'app.use(express.json({ limit: '50mb' }))' to your server code.");
            }

            console.error('SUBMIT ERROR DETAIL:', {
                status: statusCode,
                data: err.response?.data,
                message: err.message
            });

            setErrorMsg(
                serverMsg
                    ? `Server Error [${statusCode || '??'}]: ${serverMsg}`
                    : err.message
                        ? `Request Failed: ${err.message}`
                        : 'Unknown error — check browser Console (F12) for details.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (p) => {
        setTargetProduct(p);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!targetProduct) return;
        setIsDeleting(true);
        try {
            const p = targetProduct;

            // Execute Backend Purge Protocol: /api/Product/deleteProduct/:id
            await productService.deleteProduct(p._id);

            // Synchronize Local Persistence Ecosystem
            const flags = JSON.parse(localStorage.getItem('ella_product_flags') || '{}');
            const images = JSON.parse(localStorage.getItem('ella_product_images') || '{}');
            delete flags[p._id];
            delete images[p._id];
            localStorage.setItem('ella_product_flags', JSON.stringify(flags));
            localStorage.setItem('ella_product_images', JSON.stringify(images));

            // Instant UI Update - Slice the product from state array
            setProducts(prev => prev.filter(item => item._id !== p._id));

            setShowDeleteModal(false);

            // Trigger Success Cascade
            setSuccessConfig({
                title: 'Product Removed',
                message: `"${p.title}" has been permanently purged from your inventory.`
            });
            setShowSuccessModal(true);
            setTargetProduct(null);

        } catch (err) {
            console.error('DELETE ERROR:', err);
            setErrorMsg(err.response?.data?.message || 'Removal sequence failed. Potential connectivity error.');
        } finally {
            setIsDeleting(false);
        }
    };
    const nextStep = () => {
        if (isUploadingImages) {
            setWarningMsg('Please wait for image uploads to complete.');
            return;
        }
        if (formStep === 1) {
            if (!form.title.trim() || !form.categoryId) {
                setErrorMsg('Please enter Title and Category.');
                return;
            }
        }
        setErrorMsg('');
        setFormStep(prev => prev + 1);
    };

    const prevStep = () => {
        setFormStep(prev => prev - 1);
    };

    const filteredProducts = products.filter(p =>
        p.isActive !== false &&
        (
            (p.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.categoryName || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    return (
        <div className="admin-products-page">
            {/* Premium Feedback Notifications */}
            {successMsg && (
                <div className="adr-error-pop success">
                    <div className="adr-error-icon-box">
                        <FiCheck />
                    </div>
                    <div className="adr-error-text">
                        {successMsg}
                    </div>
                </div>
            )}
            {errorMsg && (
                <div className="adr-error-pop">
                    <div className="adr-error-icon-box">
                        <FiX />
                    </div>
                    <div className="adr-error-text">
                        {errorMsg}
                    </div>
                </div>
            )}
            {warningMsg && (
                <div className="adr-error-pop warning">
                    <div className="adr-error-icon-box">
                        <FiInfo />
                    </div>
                    <div className="adr-error-text">
                        {warningMsg}
                    </div>
                </div>
            )}

            {/* Premium ADR Banner */}
            <div className="adr-premium-banner">
                <div className="adr-banner-glow"></div>
                <div className="adr-banner-content">
                    <div className="adr-tag">ADMIN PANEL</div>
                    <h1 className="adr-title">Product Management</h1>
                    <p className="adr-subtitle">
                        Add and manage your products, stock, and store sections easily.
                    </p>
                    <div className="adr-features">
                        <span><FiLayers /> Easy Setup</span>
                        <span><FiZap /> Real-time</span>
                        <span><FiTrendingUp /> Statistics</span>
                    </div>
                </div>
            </div>

            {/* Header Area */}
            <div className="products-header-row">
                <div className="page-title-area">

                    <h2>Product Catalog</h2>
                </div>
                <button className="btn-create-product" onClick={() => setShowDrawer(true)}>
                    <FiPlus /> New Product
                </button>
            </div>

            {/* Premium Stats Overview */}
            <div className="products-stats-grid premium">
                <div className="premium-stat-card">
                    <div className="wave-bg"></div>
                    <div className="growth-badge positive">+15.2%</div>
                    <div className="premium-icon-box blue">
                        <FiBox className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">TOTAL PRODUCTS</span>
                            <span className="p-dot blue"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : products.length}</span>
                    </div>
                </div>

                <div className="premium-stat-card">
                    <div className="wave-bg"></div>
                    <div className="growth-badge positive">+8.4%</div>
                    <div className="premium-icon-box green">
                        <FiTrendingUp className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">NEW ITEMS</span>
                            <span className="p-dot green"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : products.filter(p => (p.isNewArrival || p.productType === 'newArrival')).length}</span>
                    </div>
                </div>

                <div className="premium-stat-card">
                    <div className="wave-bg"></div>
                    <div className="growth-badge positive">+24.7%</div>
                    <div className="premium-icon-box purple">
                        <FiCheck className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">ACTIVE PRODUCTS</span>
                            <span className="p-dot purple"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : products.filter(p => p.isActive !== false).length}</span>
                    </div>
                </div>

                <div className="premium-stat-card">
                    <div className="wave-bg"></div>
                    <div className="growth-badge negative">-3.1%</div>
                    <div className="premium-icon-box orange">
                        <FiZap className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">LOW STOCK</span>
                            <span className="p-dot orange"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : products.filter(p => p.totalStock < 5 && p.isActive !== false).length}</span>
                    </div>
                </div>
            </div>

            {/* Main Table Card */}
            <div className="products-table-card">
                <div className="table-controls">
                    <div className="table-title">
                        <h3>Product Catalog <span>({filteredProducts.length} Items)</span></h3>
                    </div>
                    <div className="search-input-wrap">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Find products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Product Details</th>
                                <th>Category</th>
                                <th>Stock</th>
                                <th>Price</th>
                                <th>Show In</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                [1, 2, 3, 4, 5].map(v => <tr key={v} style={{ height: '80px' }}><td colSpan="6"></td></tr>)
                            ) : (
                                filteredProducts.map(p => (
                                    <tr key={p._id}>
                                        <td data-label="Product Details">
                                            <div className="product-cell">
                                                <img
                                                    src={
                                                        (p.images && p.images.length > 0 && p.images[0])
                                                            ? p.images[0]
                                                            : p.image || p.imageUrl || p.thumbnail
                                                            || 'https://placehold.co/100x100?text=No+Image'
                                                    }
                                                    className="product-img"
                                                    alt={p.title}
                                                    onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=No+Image'; }}
                                                />
                                                <div className="product-info-text">
                                                    <span className="product-name">{p.title}</span>
                                                    <span className="product-category">ID: {p._id?.substring(18)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td data-label="Category">
                                            <span className="category-tag">{p.categoryName}</span>
                                        </td>
                                        <td data-label="Stock">
                                            <span className={`stock-badge ${p.totalStock > 10 ? 'in-stock' : p.totalStock > 0 ? 'low-stock' : 'out-stock'}`}>
                                                {p.totalStock} in stock
                                            </span>
                                        </td>
                                        <td data-label="Price">
                                            <span className="price-text">
                                                <span className="pkr-badge">PKR</span>
                                                {p.variants?.[0]?.price || 0}
                                            </span>
                                        </td>
                                        <td data-label="Show In">
                                            <div className="visibility-pills">
                                                {(p.isNewArrival || p.productType === 'newArrival') && <span className="vis-pill new">New</span>}
                                                {(p.isTopRated || p.productType === 'topRated') && <span className="vis-pill rated">Top Rated</span>}
                                                {(p.isBestSelling || p.productType === 'bestSelling') && <span className="vis-pill selling">Best Selling</span>}
                                            </div>
                                        </td>
                                        <td className="action-btns" data-label="Actions">
                                            <button className="btn-icon" onClick={() => handleEditClick(p)} title="Edit"><FiEdit /></button>
                                            <button className="btn-icon delete" onClick={() => handleDelete(p)} title="Delete"><FiTrash2 /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Product Creation/Edit Drawer */}
            <div className={`product-drawer-overlay ${showDrawer ? 'active' : ''}`} onClick={() => !isSubmitting && setShowDrawer(false)}>
                <div className="product-side-drawer" onClick={e => e.stopPropagation()}>
                    <header className="drawer-header">
                        <h2>{isEditMode ? 'Edit Product' : 'Add Product'}</h2>
                        <button className="btn-close-drawer" onClick={() => setShowDrawer(false)}><FiX /></button>
                    </header>

                    {/* Stepper Indicator */}
                    <div className="form-stepper-container">
                        <div className={`step-item ${formStep >= 1 ? 'active' : ''} ${formStep > 1 ? 'completed' : ''}`}>
                            <div className="step-point">1</div>
                            <span>Info</span>
                        </div>
                        <div className={`step-line ${formStep > 1 ? 'completed' : ''}`}></div>
                        <div className={`step-item ${formStep >= 2 ? 'active' : ''} ${formStep > 2 ? 'completed' : ''}`}>
                            <div className="step-point">2</div>
                            <span>Price</span>
                        </div>
                        <div className={`step-line ${formStep > 2 ? 'completed' : ''}`}></div>
                        <div className={`step-item ${formStep >= 3 ? 'active' : ''}`}>
                            <div className="step-point">3</div>
                            <span>Images</span>
                        </div>
                    </div>

                    <div className="drawer-content">
                        <form id="productForm">
                            {/* STEP 1: IDENTITY */}
                            {formStep === 1 && (
                                <div className="form-step-section">
                                    <div className="form-grid">
                                        <div className="product-form-group">
                                            <label>Product Name</label>
                                            <input
                                                className="form-input"
                                                name="title"
                                                value={form.title}
                                                onChange={handleInputChange}
                                                placeholder="Enter name..."
                                                required
                                            />
                                        </div>
                                        <div className="product-form-group">
                                            <label>Product Link (URL)</label>
                                            <input
                                                className="form-input"
                                                name="slug"
                                                value={form.slug}
                                                onChange={handleInputChange}
                                                placeholder="product-link"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="product-form-group">
                                        <label>Description</label>
                                        <textarea
                                            className="form-textarea"
                                            name="description"
                                            value={form.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                            placeholder="Enter details here..."
                                        />
                                    </div>

                                    <div className="form-grid">
                                        <div className="product-form-group">
                                            <label>Category</label>
                                            <select
                                                className="form-select"
                                                name="categoryId"
                                                value={form.categoryId}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map(cat => (
                                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="product-form-group">
                                            <label>Show in Store</label>
                                            <select
                                                className="form-select"
                                                name="isActive"
                                                value={form.isActive ? "true" : "false"}
                                                onChange={(e) => setForm(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                                            >
                                                <option value="true">Yes (Visible)</option>
                                                <option value="false">No (Hidden)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 2: PRICING & INVENTORY */}
                            {formStep === 2 && (
                                <div className="form-step-section">
                                    <div className="form-grid">
                                        <div className="product-form-group">
                                            <label className="label-with-badge">
                                                Price <span className="pkr-badge mini">PKR</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                name="price"
                                                value={form.price}
                                                onChange={handleInputChange}
                                                onFocus={(e) => {
                                                    e.target.placeholder = '';
                                                    if (form.price === '0' || form.price === '0.00' || form.price === 0) {
                                                        setForm(prev => ({ ...prev, price: '' }));
                                                    }
                                                }}
                                                onBlur={(e) => e.target.placeholder = '0.00'}
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                        <div className="product-form-group">
                                            <label>Discount (%)</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                name="discount"
                                                value={form.discount}
                                                onChange={handleInputChange}
                                                onFocus={(e) => {
                                                    e.target.placeholder = '';
                                                    if (form.discount === '0' || form.discount === 0) {
                                                        setForm(prev => ({ ...prev, discount: '' }));
                                                    }
                                                }}
                                                onBlur={(e) => e.target.placeholder = '0'}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="product-form-group">
                                        <label>Total Stock</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="stock"
                                            value={form.stock}
                                            onChange={handleInputChange}
                                            onFocus={(e) => {
                                                e.target.placeholder = '';
                                                if (form.stock === '0' || form.stock === 0) {
                                                    setForm(prev => ({ ...prev, stock: '' }));
                                                }
                                            }}
                                            onBlur={(e) => e.target.placeholder = '0'}
                                            placeholder="0"
                                        />
                                    </div>

                                    <div className="form-grid">
                                        <div className="product-form-group">
                                            <label>Available Sizes</label>
                                            <input
                                                className="form-input"
                                                name="sizes"
                                                value={form.sizes}
                                                onChange={handleInputChange}
                                                placeholder="S, M, L, XL..."
                                            />
                                            <div className="size-suggestion-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                                {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', 'One Size'].map(s => {
                                                    const isSelected = form.sizes.split(',').map(str => str.trim().toLowerCase()).includes(s.toLowerCase());
                                                    return (
                                                        <div
                                                            key={s}
                                                            onClick={() => {
                                                                let currentSizes = form.sizes.split(',').map(str => str.trim()).filter(Boolean);
                                                                if (isSelected) {
                                                                    currentSizes = currentSizes.filter(sc => sc.toLowerCase() !== s.toLowerCase());
                                                                } else {
                                                                    currentSizes.push(s);
                                                                }
                                                                setForm(prev => ({ ...prev, sizes: currentSizes.join(', ') }));
                                                            }}
                                                            style={{
                                                                padding: '8px 16px',
                                                                borderRadius: '10px',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 700,
                                                                cursor: 'pointer',
                                                                background: isSelected ? '#000' : '#fff',
                                                                color: isSelected ? '#fff' : '#64748b',
                                                                border: isSelected ? '1px solid #000' : '1px solid #e2e8f0',
                                                                transition: 'all 0.2s',
                                                                boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                                                            }}
                                                        >
                                                            {s}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="product-form-group">
                                            <label>Available Colors</label>
                                            <input
                                                className="form-input"
                                                name="colors"
                                                value={form.colors}
                                                onChange={handleInputChange}
                                                placeholder="Black, White, Red..."
                                            />
                                            <div className="color-palette-suggestions" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
                                                {['Black', 'White', 'Gray', 'Silver', 'Charcoal', 'Ivory', 'Beige', 'Brown', 'Tan', 'Camel', 'Red', 'Maroon', 'Burgundy', 'Blue', 'Navy', 'Teal', 'Cyan', 'Indigo', 'Green', 'Olive', 'Mint', 'Yellow', 'Mustard', 'Gold', 'Orange', 'Coral', 'Peach', 'Purple', 'Lavender', 'Pink', 'Rose Gold', 'Magenta', 'Bronze', 'Copper', 'Khaki'].map(c => {
                                                    const isSelected = form.colors.toLowerCase().includes(c.toLowerCase());
                                                    const colorsMap = {
                                                        Black: '#1e293b', White: '#ffffff', Gray: '#94a3b8', Silver: '#cbd5e1', Charcoal: '#374151',
                                                        Ivory: '#fffff0', Beige: '#f5f5dc', Brown: '#78350f', Tan: '#d2b48c', Camel: '#c19a6b',
                                                        Red: '#ef4444', Maroon: '#7f1d1d', Burgundy: '#800020', Blue: '#3b82f6', Navy: '#1e3a8a',
                                                        Teal: '#14b8a6', Cyan: '#06b6d4', Indigo: '#4f46e5', Green: '#22c55e', Olive: '#65a30d',
                                                        Mint: '#34d399', Yellow: '#facc15', Mustard: '#ffdb58', Gold: '#d4af37', Orange: '#f97316',
                                                        Coral: '#ff7f50', Peach: '#ffdab9', Purple: '#a855f7', Lavender: '#e6e6fa', Pink: '#ec4899',
                                                        'Rose Gold': '#b76e79', Magenta: '#d946ef', Bronze: '#cd7f32', Copper: '#b87333', Khaki: '#f0e68c'
                                                    };
                                                    return (
                                                        <div
                                                            key={c}
                                                            title={c}
                                                            onClick={() => {
                                                                let currentColors = form.colors.split(',').map(s => s.trim()).filter(Boolean);
                                                                if (isSelected) {
                                                                    currentColors = currentColors.filter(sc => sc.toLowerCase() !== c.toLowerCase());
                                                                } else {
                                                                    currentColors.push(c);
                                                                }
                                                                setForm(prev => ({ ...prev, colors: currentColors.join(', ') }));
                                                            }}
                                                            className={`color-swatch ${isSelected ? 'selected' : ''}`}
                                                            style={{
                                                                backgroundColor: colorsMap[c] || '#ccc',
                                                                width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer',
                                                                border: isSelected ? '2px solid transparent' : '2px solid #e2e8f0',
                                                                boxShadow: isSelected ? '0 0 0 2px #38bdf8' : '0 2px 5px rgba(0,0,0,0.05)',
                                                                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: PLACEMENT & VISUALS */}
                            {formStep === 3 && (
                                <div className="form-step-section">
                                    <div className="product-form-group">
                                        <label>Show in Sections</label>
                                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '-4px', marginBottom: '12px' }}>
                                            Choose where to show this product.
                                        </p>
                                        <div className="placement-selection-grid">
                                            <div className={`placement-chip ${form.isNewArrival ? 'active' : ''}`} onClick={() => handleToggleFlag('isNewArrival')}>
                                                <FiZap /> New Arrivals
                                            </div>
                                            <div className={`placement-chip ${form.isBestSelling ? 'active' : ''}`} onClick={() => handleToggleFlag('isBestSelling')}>
                                                <FiShoppingBag /> Best Selling
                                            </div>
                                            <div className={`placement-chip ${form.isTopRated ? 'active' : ''}`} onClick={() => handleToggleFlag('isTopRated')}>
                                                <FiStar /> Top Rated
                                            </div>
                                        </div>
                                    </div>

                                    <div className="product-form-group">
                                        <label>Product Images</label>
                                        <div className="image-upload-area" onClick={() => fileInputRef.current.click()}>
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                ref={fileInputRef}
                                                onChange={handleImageUpload}
                                                style={{ display: 'none' }}
                                            />
                                            <FiImage style={{ fontSize: '32px', color: isUploadingImages ? '#3b82f6' : '#94a3b8', marginBottom: '8px' }} />
                                            {isUploadingImages ? (
                                                <>
                                                    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#3b82f6' }}>Processing...</p>
                                                    <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>Please wait a moment</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Click to select images</p>
                                                    <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>PNG, JPG or WEBP supported (Max 5)</p>
                                                </>
                                            )}
                                        </div>

                                        <div className="images-preview-grid">
                                            {imagePreviews.map((img, idx) => (
                                                <div key={idx} className={`preview-box ${img.source === 'rejected' ? 'is-rejected' : ''}`}>
                                                    {img.source === 'rejected' && (
                                                        <div className="rejection-overlay-boutique" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(239, 68, 68, 0.8)', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', zIndex: 10 }}>
                                                            <FiX style={{ fontSize: '24px', marginBottom: '4px' }} />
                                                            <span style={{ fontWeight: 700, fontSize: '0.75rem' }}>REJECTED</span>
                                                        </div>
                                                    )}
                                                    <img src={img.url} alt="preview" />
                                                    <div className={`sync-status ${img.source}`}>
                                                        {img.source === 'cloud' ? <FiCloud title="Saved Online" /> : (img.source === 'rejected' ? <FiX title="Rejected" style={{color:'red'}} /> : <FiHardDrive title="Saved Locally" />)}
                                                    </div>
                                                    <button type="button" className="remove-img" onClick={() => removeImage(idx)} style={{ zIndex: 11 }}><FiX /></button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* --- NEW: COLOR TO IMAGE MAPPING --- */}
                                    {form.colors && form.colors.trim() !== '' && imagePreviews.length > 0 && (
                                        <div className="product-form-group" style={{ marginTop: '24px', padding: '20px', background: '#f8fafc', borderRadius: '20px', border: '1px dashed #e2e8f0' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', marginBottom: '16px' }}>
                                                <FiZap style={{ color: '#f59e0b' }} /> Color Visual Mapping
                                            </label>
                                            <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '16px' }}>
                                                Select the primary image for each color. This image will show when customers select the color.
                                            </p>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                {form.colors.split(',').map(c => c.trim()).filter(Boolean).map(color => (
                                                    <div key={color} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: '#fff', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: color.toLowerCase(), border: '1px solid #eee' }}></div>
                                                            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{color}</span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '200px', padding: '4px' }}>
                                                            {imagePreviews.map((img, idx) => (
                                                                <img
                                                                    key={idx}
                                                                    src={img.url}
                                                                    onClick={() => setForm(prev => ({
                                                                        ...prev,
                                                                        colorImageMap: { ...prev.colorImageMap, [color]: img.url }
                                                                    }))}
                                                                    style={{
                                                                        width: '32px',
                                                                        height: '32px',
                                                                        objectFit: 'cover',
                                                                        borderRadius: '6px',
                                                                        cursor: 'pointer',
                                                                        border: form.colorImageMap[color] === img.url ? '2px solid #3b82f6' : '1px solid #eee',
                                                                        opacity: form.colorImageMap[color] === img.url ? 1 : 0.5,
                                                                        transition: 'all 0.2s'
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </form>
                    </div>

                    <div className="drawer-footer-actions">
                        {formStep > 1 ? (
                            <button type="button" className="btn-cancel" onClick={prevStep} disabled={isSubmitting}>Back</button>
                        ) : (
                            <button type="button" className="btn-cancel" onClick={() => !isSubmitting && setShowDrawer(false)}>Cancel</button>
                        )}

                        {formStep < 3 ? (
                            <button type="button" className="btn-submit" onClick={nextStep} disabled={isUploadingImages}>
                                {isUploadingImages ? 'Uploading...' : 'Next Step'}
                            </button>
                        ) : (
                            <button 
                                type="button" 
                                className="btn-submit" 
                                onClick={handleSubmit} 
                                disabled={isSubmitting || isUploadingImages || imagePreviews.some(p => p.source === 'rejected')}
                            >
                                {isSubmitting ? 'Saving...' : (isUploadingImages ? 'Uploading...' : (isEditMode ? 'Save Changes' : 'Add Product'))}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* Delete Confirmation Modal */}
            <div className={`delete-modal-overlay ${showDeleteModal ? 'active' : ''}`} onClick={() => setShowDeleteModal(false)}>
                <div className="delete-modal-card" onClick={e => e.stopPropagation()}>
                    <div className="delete-modal-icon-box">
                        <FiTrash2 className="animated-trash-icon" />
                    </div>
                    <h3>Delete Product</h3>
                    <p>Are you sure you want to delete <strong>{targetProduct?.title}</strong>? It will be removed from your store.</p>
                    <div className="delete-modal-actions">
                        <button className="btn-modal-cancel" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>Cancel</button>
                        <button className="btn-modal-confirm" onClick={confirmDelete} disabled={isDeleting}>
                            {isDeleting ? 'Removing...' : 'Yes, Delete'}
                        </button>
                    </div>
                </div>
            </div>
            {/* Success Modal */}
            <div className={`success-modal-overlay ${showSuccessModal ? 'active' : ''}`} onClick={() => setShowSuccessModal(false)}>
                <div className="success-modal-card" onClick={e => e.stopPropagation()}>
                    <div className="success-icon-box">
                        <FiCheck className="success-check-icon" />
                    </div>
                    <h3>{successConfig.title}</h3>
                    <p>{successConfig.message}</p>
                    <button className="btn-success-ack" onClick={() => setShowSuccessModal(false)}>OK</button>
                </div>
            </div>
        </div>
    );
};

export default AdminProducts;


